import React, { useEffect, useRef, useState } from "react";
import Drawer from "react-modern-drawer";
import "react-modern-drawer/dist/index.css";
import { useForm } from "react-hook-form";
import { translateData, getLanguages, addPhase, updatePhase } from "../lib/api";
import { Icon } from "@iconify/react/dist/iconify.js";

const customStyles = `
  .form-grid-container {
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 1.5rem !important;
    width: 100% !important;
    margin-bottom: 1.25rem !important;
  }
  .form-control, textarea {
    width: 100% !important;
    border-radius: 8px !important;
    padding: 8px 12px !important;
    border: 1px solid #ced4da !important;
    background: #fff !important;
  }
  .error-message { color: #dc3545; font-size: 0.75rem; }
  .success-message { color: #28a745; text-align: center; margin-bottom: 1rem; }

  /* Main Tabs (English / Other Languages) */
  .main-tabs { display: flex; gap: 8px; margin-bottom: 12px; }
  .main-tab {
    padding: 8px 14px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 600;
  }
  .main-tab.active {
    background: #e8f0fe;
    border-color: #1a73e8;
    color: #1a73e8;
  }

  /* Language Sub-Tabs */
  .lang-tabs { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
  .lang-tab {
    padding: 6px 10px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 500;
  }
  .lang-tab.active {
    background: #e8f0fe;
    border-color: #1a73e8;
    color: #1a73e8;
  }
`;

export default function AddPhase({ isOpen, onClose, phaseToEdit, refresh }) {
  const [languages, setLanguages] = useState([]);
  const [translations, setTranslations] = useState({ PhaseName: {} });
  const [selectedLang, setSelectedLang] = useState("");
  const [step, setStep] = useState("basic"); // basic → translate → translations
  const [mainTab, setMainTab] = useState("english");
  const [message, setMessage] = useState(null);

  const nameInputRef = useRef(null);

  const { register, handleSubmit, setValue, getValues, reset, formState: { errors } } = useForm({
    defaultValues: { name: "", priority: 1 },
  });

  const selectedLangName = languages.find(l => l.GoogleLanguageCode === selectedLang)?.LanguageName || selectedLang || '';
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => nameInputRef.current && nameInputRef.current.focus(), 100);

      reset();
      setTranslations({ PhaseName: {} });
      setMainTab("english");
      setStep("basic");
      setMessage(null);

      getLanguages().then((res) => {
        const list = res.data || res;
        setLanguages(list);
        if (list.length > 0) setSelectedLang(list[0].GoogleLanguageCode);
      });

      if (phaseToEdit) {
        setValue("name", phaseToEdit.name);
        setValue("priority", phaseToEdit.priority);

        const otherLang = phaseToEdit.otherLang?.PhaseName || {};
        setTranslations({ PhaseName: otherLang });
      }
    }
  }, [isOpen, phaseToEdit]);

  const onInitialTranslate = async (data) => {
    if (languages.length === 0) {
      setMessage({ type: 'error', text: 'Languages not loaded yet. Please try again.' });
      return;
    }

    setStep("translate");

    const result = await translateData({
      PhaseName: data.name,
    });

    const translated = result?.data || result || {};

    const final = {};

    languages.forEach((lang) => {
      final[lang.GoogleLanguageCode] =
        translated.PhaseName?.[lang.GoogleLanguageCode] || data.name;
    });

    setTranslations({ PhaseName: final });
    setStep("translations");
    setMainTab("other");
  };

  const onFinalSave = async () => {
    const name = getValues("name");
    const priority = Number(getValues("priority"));

    const payload = {
      PhaseName: name,
      Priority: priority,
      PhaseNameOtherLang: { PhaseName: translations.PhaseName },
      ...(phaseToEdit ? { ID: phaseToEdit.id } : {}),
    };

    const token = localStorage.getItem("authToken") || localStorage.getItem("token");

    try {
      if (phaseToEdit) await updatePhase(token, payload);
      else await addPhase(token, payload);

      setMessage({
        type: 'success',
        text: phaseToEdit ? 'Phase updated successfully!' : 'Phase added successfully!'
      });

      setTimeout(() => {
        refresh();
        onClose();
        setMessage(null);
      }, 1200);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save phase' });
    }
  };

  const isTranslationsValid = () => {
    return languages.every((lang) => {
      const code = lang.GoogleLanguageCode;
      return translations.PhaseName[code]?.trim();
    });
  };

  return (
    <>
      <style>{customStyles}</style>

      <Drawer open={isOpen} direction="right" size={900} className="p-24">
        <div className="card-body">
          <button
            variant="outline"
            onClick={onClose}
            style={{ border: 'none', color: '#000', padding: 0, textAlign: 'end', padding: '15px', position: 'absolute', top: 0, right: 0 }}
          >
            <Icon icon="lucide:x" className="text-2xl" />
          </button>
          <h6 className="text-xxl text-primary-light mb-16">
            {phaseToEdit ? 'Edit Phase' : 'Add New Phase'}
          </h6>

          {message && (
            <div className={message.type === 'success' ? 'success-message' : 'error-message'}>
              {message.text}
            </div>
          )}
          {/* Main Tabs: English / Other Languages */}
          <div className="main-tabs">
            <button
              type="button"
              className={`main-tab ${mainTab === 'english' ? 'active' : ''}`}
              onClick={() => setMainTab('english')}
            >
              English
            </button>
            <button
              type="button"
              className={`main-tab ${mainTab === 'other' ? 'active' : ''}`}
              onClick={() => setMainTab('other')}
              disabled={!phaseToEdit && step !== 'translations'}
              title={!phaseToEdit && step !== 'translations' ? 'Translate first to enable' : 'Other languages'}
            >
              Other Languages
            </button>
          </div>

          {/* Translating loader */}
          {step === 'translate' && (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <h4>Translating…</h4>
              <p>Please wait...</p>
            </div>
          )}

          {/* ENGLISH TAB */}
          {mainTab === 'english' && step !== 'translate' && (
            <form onSubmit={handleSubmit(onInitialTranslate)}>
              {/* Name + Priority */}
              <div className="form-grid-container">
                <div>
                  <label className="form-label">Phase Name *</label>
                  <input
                    className="form-control"
                    {...register("name", {
                      required: 'Required',
                      minLength: { value: 2, message: 'Min 2 chars' }
                    })}
                    ref={(e) => {
                      register("name").ref(e);
                      nameInputRef.current = e;
                    }}
                  />
                  {errors.name && <span className="error-message">{errors.name.message}</span>}
                </div>

                <div>
                  <label className="form-label">Priority *</label>
                  <input
                    type="number"
                    className="form-control"
                    {...register("priority", {
                      required: 'Required',
                      min: { value: 1, message: 'Minimum 1' },
                      max: { value: 100, message: 'Maximum 100' }
                    })}
                  />
                  {errors.priority && <span className="error-message">{errors.priority.message}</span>}
                </div>
              </div>

              {/* Buttons */}
              <div className="d-flex justify-content-center gap-3">
                <button
                  type="button"
                  className="border border-danger-600 text-danger-600 px-56 py-10 radius-8"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-yellow px-56 py-12"
                >
                  Translate
                </button>
              </div>
            </form>
          )}

          {/* OTHER LANGUAGES TAB */}
          {mainTab === 'other' && (
            <>
              {/* Language Sub-Tabs */}
              <div className="lang-tabs">
                {languages.length === 0 ? (
                  <span style={{ opacity: 0.7 }}>No languages loaded</span>
                ) : (
                  languages.map((lang) => (
                    <button
                      key={lang.GoogleLanguageCode}
                      type="button"
                      className={`lang-tab ${selectedLang === lang.GoogleLanguageCode ? 'active' : ''}`}
                      onClick={() => setSelectedLang(lang.GoogleLanguageCode)}
                    >
                      {lang.LanguageName}
                    </button>
                  ))
                )}
              </div>

              {/* Translation Fields */}
              {selectedLang ? (
                <>
                  <div className="mb-20">
                    <label className="form-label">Phase Name ({selectedLangName})</label>
                    <input
                      className="form-control"
                      value={translations.PhaseName[selectedLang] || ""}
                      onChange={(e) =>
                        setTranslations((prev) => ({
                          PhaseName: {
                            ...prev.PhaseName,
                            [selectedLang]: e.target.value,
                          },
                        }))
                      }
                    />
                  </div>
                </>
              ) : (
                <div style={{ opacity: 0.7, marginBottom: 12 }}>Select a language</div>
              )}

              {/* Buttons */}
              <div className="d-flex justify-content-center gap-3">
                <button
                  type="button"
                  className="border border-danger-600 text-danger-600 px-56 py-10 radius-8"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-yellow px-56 py-12"
                  onClick={onFinalSave}
                  disabled={!isTranslationsValid()}
                  title={!isTranslationsValid() ? "Please fill all fields for all languages" : ""}
                  style={{ opacity: !isTranslationsValid() ? 0.5 : 1 }}
                >
                  Save
                </button>
              </div>
            </>
          )}
        </div>
      </Drawer>
    </>
  );
}